import styled from "@emotion/styled";
import type { FC } from "react";
import { APP_BREAKPOINT } from "src/constants/layout";

/**
 * Size variant for the avatar stack.
 *
 * - `sm` (default): mobile 24px / desktop 32px avatars. Used inside the
 *   Care Team card badge where space is tight.
 * - `lg`: 40px avatars regardless of viewport. Used in the Care Team
 *   detail bottom sheet header where the stack anchors the sheet.
 */
type AvatarStackSize = "sm" | "lg";

/**
 * Avatar + pill dimensions are driven by CSS custom properties so avatars,
 * negative-margin overlap, and the trailing label pill stay in lockstep.
 * `data-size` selects the token set; the `sm` values also bump on desktop
 * to match the larger card artwork.
 */
const Stack = styled.div`
  --avatar-size: 1.5rem;
  --avatar-overlap: 0.5rem;
  --avatar-font-size: 0.5625rem;

  display: inline-flex;
  align-items: center;

  &[data-size="lg"] {
    --avatar-size: 2.5rem;
    --avatar-overlap: 0.75rem;
    --avatar-font-size: 0.875rem;
  }

  @media (min-width: ${APP_BREAKPOINT}px) {
    --avatar-size: 2rem;
    --avatar-overlap: 0.625rem;
    --avatar-font-size: 0.75rem;

    &[data-size="lg"] {
      --avatar-size: 2.5rem;
      --avatar-overlap: 0.75rem;
      --avatar-font-size: 0.875rem;
    }
  }
`;

const Avatar = styled.img`
  width: var(--avatar-size);
  height: var(--avatar-size);
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid var(--color-cream, #fff);
  background: var(--color-cream, #fff);
  box-sizing: border-box;

  &:not(:first-of-type) {
    margin-left: calc(var(--avatar-overlap) * -1);
  }
`;

/**
 * Perfect-circle pill that sits at the end of the avatar stack.
 * Same height AND width so it renders as a circle regardless of label
 * length. Labels are short by design ("+55", "24/7") — if a longer label
 * is passed the text will crop; grow this size or rethink the badge.
 */
const LabelPill = styled.span`
  margin-left: calc(var(--avatar-overlap) * -1);
  width: var(--avatar-size);
  height: var(--avatar-size);
  border-radius: 50%;
  background: var(--color-gray-fog, #f5f5f5);
  color: var(--color-charcoal-80, #333);
  font-family: var(--font-family);
  font-size: var(--avatar-font-size);
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: 0 0 auto;
  border: 1px solid white;
`;

interface Props {
  /** Up to 4 image URLs. Extras are ignored. */
  avatars: string[];
  /** Text rendered inside the trailing circular pill (e.g. "+55", "24/7"). */
  label: string;
  /** Size variant — `sm` for the card, `lg` for the detail sheet. */
  size?: AvatarStackSize;
  /**
   * When true, omit the trailing label pill entirely. Used in the Sophie
   * detail-sheet header where the count pill is replaced by a wider
   * "Always on | 24/7" pill rendered as a sibling of the avatar stack.
   */
  hideLabel?: boolean;
}

const AvatarStack: FC<Props> = ({
  avatars,
  label,
  size = "sm",
  hideLabel = false
}) => {
  const visibleAvatars = avatars.slice(0, 4);

  return (
    <Stack data-size={size}>
      {visibleAvatars.map((src, index) => (
        <Avatar key={`${src}-${index}`} src={src} alt="" />
      ))}
      {!hideLabel && <LabelPill>{label}</LabelPill>}
    </Stack>
  );
};

export default AvatarStack;
